import { memo, useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormBuilderCardWrapper } from '../../../FormBuilderCardWrapper';
import { FormBuilderElement } from '../../../FormBuilderElement';
import {
  useFormBuilderFields,
  useFormBuilderFieldWithError,
} from '../../../hooks';
import { EFormBuilderFieldType, TFormBuilderTextEditorField } from '@/types';

type TProps = {
  field: TFormBuilderTextEditorField;
  index: number;
};

export const FormBuilderTextEditor =
  /**
   * Text editor field component for form builder with rich text editing capabilities
   * @param {TProps} props - Component props
   * @param {TFormBuilderTextEditorField} props.field - Text editor field data
   * @param {number} props.index - Field index in the form
   * @returns {JSX.Element} Form builder text editor field component
   */
  memo(({ field, index }: TProps) => {
    const { t } = useTranslation();
    const inputId = useId();
    const [active, setActive] = useState(false);
    const onActiveChange = useCallback(
      (value: boolean) => setActive(value),
      [],
    );

    const error =
      useFormBuilderFieldWithError<TFormBuilderTextEditorField>(index);

    const { changeFields, removeField, copyField, moveDownField, moveUpField } =
      useFormBuilderFields(field);

    const changeMultilineInputFields = useCallback(
      (updated: Partial<TFormBuilderTextEditorField>) => {
        changeFields({ ...field, ...updated });
      },
      [changeFields, field],
    );
    return (
      <FormBuilderCardWrapper
        variant={EFormBuilderFieldType.TEXT_EDITOR}
        error={!!error}
        active={active}
        onActiveChange={onActiveChange}
        onRemove={removeField}
        onDownMove={moveDownField}
        onUpMove={moveUpField}
        onDuplicate={copyField}
      >
        <FormBuilderElement
          label={
            <FormBuilderElement.Label
              label={field.label}
              htmlFor={`${inputId}-input`}
              onLabelChange={(label) => changeMultilineInputFields({ label })}
              placeholder={t('form-builder.your-label-here')}
              required={field.required}
              error={!!error?.label}
            />
          }
          required={field.required}
          onRequiredChange={(required) =>
            changeMultilineInputFields({ required })
          }
        >
          <FormBuilderElement.MultiLineInput
            id={inputId}
            placeholder={t('form-builder.placeholder-text')}
            value={field.placeholder}
            onChange={(e) =>
              changeMultilineInputFields({ placeholder: e.target.value })
            }
          />
        </FormBuilderElement>
      </FormBuilderCardWrapper>
    );
  });

FormBuilderTextEditor.displayName = 'FormBuilderTextEditor';
